import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UntypedFormGroup } from '@angular/forms';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { shareReplay, tap, switchMap, map, catchError, finalize } from 'rxjs/operators';

import { StorageService } from './storage.service';

import { Customer } from 'src/app/domain/Customer';
import { OrderDetail } from 'src/app/domain/OrderDetail';
import { CustomerAddress } from 'src/app/domain/CustomerAddress';
import { Address } from 'src/app/domain/Address';
import { Card } from 'src/app/domain/Card';

import {
  GlovoBagsMasterModalComponent,
} from 'src/app/shared/modals/glovo-bags-master-modal/glovo-bags-master-modal.component';

import {
  GlovoBagsVendorModalComponent,
} from 'src/app/shared/modals/glovo-bags-vendor-modal/glovo-bags-vendor-modal.component';

export interface CustomerResponse {
  customer: Customer;
}

export interface OrdersResponse {
  orders: OrderDetail[];
}

export interface CardsResponse {
  customerCards: Card[];
}

export interface TokenResponse {
  token: string;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(NgbModal);
  private readonly storageService = inject(StorageService);

  private readonly loggedCustomerSubject$ = new BehaviorSubject<Customer | null>(
    this.storageService.getGlobalItem<Customer>('customer') ?? null
  );
  public loggedCustomer$ = this.loggedCustomerSubject$.asObservable();

  constructor() {
    const token = this.storageService.getGlobalItem<string>('token');

    if (!token) {
      this.loggedCustomerSubject$.next(null);
    }
  }

  get loggedCustomer() {
    return this.loggedCustomerSubject$.getValue();
  }

  get currentCustomer(): Customer | null {
    return this.loggedCustomerSubject$.getValue();
  }

  get currentGuestCustomer(): Customer {
    return this.storageService.getItem<Customer>('guestCustomer');
  }

  public static isRefreshTokenRequest(url: string): boolean {
    return url.endsWith('api/token/refresh');
  }

  setLoggedCustomer(customer: Customer, isLogin = false) {
    if (customer) {
      this.storageService.setGlobalItem('customer', customer);
    } else {
      this.storageService.removeGlobalItem('customer');
    }
    this.loggedCustomerSubject$.next(customer);
    if (isLogin && customer.slaves?.some((x) => x.procurementCreditsSfTotal > 0)) {
      const modalRef = this.modalService.open(GlovoBagsMasterModalComponent, {
        windowClass: 'modal-glovo-bags',
      });
      modalRef.componentInstance.customer = customer;
    }
    if (
      isLogin &&
      customer.vendorSubAccounts?.subAccounts?.length > 1 &&
      customer.vendorSubAccounts?.subAccounts?.some((x) => x.procurementCreditsSf > 0)
    ) {
      const modalRef = this.modalService.open(GlovoBagsVendorModalComponent, {
        windowClass: 'glovo-bags-vendor-modal',
      });
      modalRef.componentInstance.customer = customer;
    }
  }

  public login(params: any): Observable<any> {
    return this.http.post<TokenResponse>('login', params).pipe(
      tap((response) => {
        this.storageService.setGlobalItem('token', response.token);
        this.storageService.setGlobalItem('refreshToken', response.refresh_token);
      }),
      switchMap(() =>
        this.getLoggedCustomer().pipe(
          tap((data: CustomerResponse) => {
            this.setLoggedCustomer(data.customer, true);
          })
        )
      )
    );
  }

  checkHash(hash: string) {
    return this.http.post<{ token: string | null }>(`customer/check`, { hash });
  }

  public autoLoginWithHash(hash: string): Observable<CustomerResponse> {
    return this.checkHash(hash).pipe(
      switchMap(({ token }) => {
        if (!token) {
          this.removeCustomerData();
          return of({ customer: null } as CustomerResponse);
        }

        this.storageService.setGlobalItem('token', token);
        return this.getLoggedCustomer();
      }),
      tap((data: CustomerResponse) => {
        if (data?.customer) {
          this.setLoggedCustomer(data.customer, true);
        }
      })
    );
  }

  public refreshToken(refreshToken: string): Observable<TokenResponse> {
    return this.http.post<TokenResponse>('api/token/refresh', { refresh_token: refreshToken }).pipe(
      tap((response) => {
        this.storageService.setGlobalItem('token', response.token);
        this.storageService.setGlobalItem('refreshToken', response.refresh_token);
      }),
      catchError((error) => {
        console.error('Error al refrescar el token:', error);
        this.removeCustomerData();
        return throwError(error);
      })
    );
  }

  public register(params: { name: any; password: any; emailLogin: any; country: any }) {
    return this.http.post('customer/register', params);
  }

  public activate(params: { hash: string }): Observable<CustomerResponse> {
    return this.http.post<TokenResponse>('customer/activate', params).pipe(
      switchMap((response: TokenResponse) => {
        if (!response?.token || !response?.refresh_token) {
          this.removeCustomerData();
          return throwError(() => new Error('Activation response missing token or refresh_token'));
        }

        this.storageService.setGlobalItem('token', response.token);
        this.storageService.setGlobalItem('refreshToken', response.refresh_token);

        return this.getLoggedCustomer();
      }),
      tap((data: CustomerResponse) => {
        if (data?.customer) {
          this.setLoggedCustomer(data.customer, true);
        }
      })
    );
  }

  public recoverPassword(params: any) {
    return this.http.post('customer/pass_recovery', params);
  }

  public recoverPasswordCheck(hash: string) {
    return this.http.post('customer/pass_recovery_check', { hash }).pipe(shareReplay());
  }

  public recoverPasswordCreate(hash: string, password: string) {
    return this.http.post('customer/pass_recovery_create', { hash, password }).pipe(
      shareReplay(),
      map((res: any) => res.token),
      tap((token: string) => this.storageService.setGlobalItem('token', token)),
      switchMap(() => this.getLoggedCustomer())
    );
  }

  public getLoggedCustomer() {
    const token = this.storageService.getGlobalItem<string>('token');
    if (!token) {
      return of({ customer: null } as CustomerResponse);
    }
    const request = this.http.get<CustomerResponse>('customer/login_check').pipe(shareReplay());
    request.subscribe({
      next: (data) => {
        const stillHasToken = this.storageService.getGlobalItem<string>('token');
        if (stillHasToken) {
          this.setLoggedCustomer(data.customer);
        }
      },
      error: () => {
        this.removeCustomerData();
      },
    });
    return request;
  }

  public addCustomerAddress(customerAddress: CustomerAddress) {
    return this.http.post('customer/address/add', customerAddress);
  }

  public editCustomerAddress(customerAddress: CustomerAddress) {
    return this.http.post('customer/address/edit', customerAddress);
  }

  public removeCustomerAddress(customerAddressId: number) {
    return this.http.post('customer/address/remove', { customerAddressId });
  }

  public setDefaultShippingAddress(customerAddressId: number) {
    return this.http.post('customer/address/default/shipping', { customerAddressId, action: 'set' });
  }

  public removeDefaultShippingAddress(customerAddressId: number) {
    return this.http.post('customer/address/default/shipping', { customerAddressId, action: 'remove' });
  }

  public setDefaultBillingAddress(customerAddressId: number) {
    return this.http.post('customer/address/default/billing', { customerAddressId, action: 'set' });
  }

  public removeDefaultBillingAddress(customerAddressId: number) {
    return this.http.post('customer/address/default/billing', { customerAddressId, action: 'remove' });
  }

  public getOrders(paymentMethod?: string) {
    let url = 'customer/orders';
    if (paymentMethod) {
      url += `?payment_method=${paymentMethod}`;
    }
    return this.http.get(url);
  }

  public landingForm(params: { landingSlug: string; formData: any; formValue: any }) {
    return this.http.post('customer/landing-form', params);
  }

  public addCardToken(cardId: string) {
    return this.http.post('customer/card/add', { cardId });
  }

  public deleteCardToken(cardId: string) {
    return this.http.post('customer/card/delete', { cardId });
  }

  public setDefaultCardToken(cardId: string) {
    return this.http.post('customer/card/default', { cardId });
  }

  public getCustomerProcessOutTokens() {
    return this.http.get('customer/cards');
  }

  public logout$() {
    const token = this.storageService.getGlobalItem<string>('token');
    const refreshToken = this.storageService.getGlobalItem<string>('refreshToken');

    if (!token) {
      this.removeCustomerData();
      return of(null);
    }

    return (refreshToken
        ? this.http.post('logout', { refreshToken })
        : of(null)
    ).pipe(
      catchError((error) => {
        console.error('Error during logout:', error);
        return of(null);
      }),
      finalize(() => {
        this.removeCustomerData();
      }),
      map(() => null)
    );
  }

  public removeCustomerData() {
    const customer = this.storageService.getGlobalItem<Customer>('customer');
    if (customer?.country?.code) {
      this.storageService.removeItem('cart', { countryCode: customer.country.code });
    }
    this.storageService.removeGlobalItem('token');
    this.storageService.removeGlobalItem('refreshToken');
    this.storageService.removeGlobalItem('customer');
    this.storageService.removeItem('orderTrackerPopupSeen');
    this.setLoggedCustomer(null);
  }

  setAddressFromAutocompleteGoogle(address: Address, addressForm: UntypedFormGroup) {
    addressForm.patchValue({
      address: address.number ? `${address.street}, ${address.number}` : address.street,
      postalCode: address.postalCode ?? '',
      city: address.locality ?? '',
      province: address.province ?? '',
    }, { emitEvent: true });

    const fields = ['address', 'postalCode', 'city', 'province'];
    fields.forEach(f => {
      const c = addressForm.get(f);
      if (!c) return;
      c.markAsDirty({ onlySelf: true });
      c.markAsTouched({ onlySelf: true });
      c.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    });

    addressForm.markAsTouched();
    addressForm.updateValueAndValidity({ emitEvent: true });
  }

  public changePassword(params: { password: string; newPassword: string }) {
    return this.http.post('customer/change-password', params);
  }

  public changeCommunicationEmail(params: { communicationEmail: string; }) {
    return this.http.post('customer/change-communication-email', params);
  }
}
